import { Select, SelectItem, SelectSection } from "@heroui/react";

import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { useRef } from "react";
import {
  Control,
  FieldValues,
  Path,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { useTranslation as useI18n } from "react-i18next";
import { mergeFieldOptions } from "./utils";
import type { FieldProps, SectionItem, TSections } from "./types";

type SelectFieldWithSectionProps<T extends FieldValues> = {
  primary_key?: string;
  name: Path<T>;
  label: string;
  sections: TSections;
  control: Control<T>;
  isRequired?: boolean;
  placeholder?: string;
  listboxWrapperClassNames?: string;
  allow_empty_option?: boolean;
} & FieldProps;

const DELIMITER = " -- ";

const buildSectionItemKey = (
  sectionName: string,
  itemLabel: string,
): string => {
  if (sectionName.indexOf(DELIMITER) !== -1) {
    console.error(`Internal error, modify DELIMITER present in ${sectionName}`);
  }

  return `${sectionName}${DELIMITER}${itemLabel}`;
};

const stripSectionNameFromKey = (value: string): string => {
  const [, ...rest] = value.split(DELIMITER);
  return rest.length > 0 ? rest.join(DELIMITER) : value;
};

const SelectFieldWithSection = <T extends FieldValues>({
  primary_key, // FIXME: this value is eithed undefined or asset_class1 => maybe a component with a different name should be used when primary_key is defined
  name,
  label,
  sections,
  control,
  isRequired = false,
  placeholder,
  listboxWrapperClassNames,
  fieldOptions,
  multiFieldOptions,
  className,
  allow_empty_option = false,
}: SelectFieldWithSectionProps<T>) => {
  const { setValue } = useFormContext();
  const { t } = useI18n();
  const translatedLabel = t(label, { defaultValue: label });
  const watchedPrimaryValue = useWatch({
    control,
    name: primary_key as Path<T>,
  });
  const translatedPlaceholder = placeholder
    ? t(placeholder, { defaultValue: placeholder })
    : placeholder;

  const opt = mergeFieldOptions(name, fieldOptions, multiFieldOptions);
  const listboxRef = useRef<HTMLDivElement>(null);

  if (opt?.isVisible === false) return undefined;

  return (
    <div className={className}>
      <FormField
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Select
                label={translatedLabel}
                variant="bordered"
                labelPlacement="outside"
                radius="full"
                size="sm"
                isDisabled={opt?.isDisabled}
                classNames={{
                  base: "min-w-36",
                  value: "text-foreground-800",
                  listboxWrapper: listboxWrapperClassNames,
                }}
                popoverProps={{
                  classNames: {
                    content:
                      "w-auto max-w-[var(--radix-popper-available-width)] !min-w-[210px]",
                  },
                  ref: listboxRef,
                  shouldFlip: true,
                  offset: 5,
                }}
                listboxProps={{
                  classNames: {
                    base: "max-w-none w-auto",
                  },
                }}
                isRequired={isRequired}
                placeholder={translatedPlaceholder}
                selectedKeys={(() => {
                  // controls which option the HeroUI Select shows as currently selected.
                  const v = field.value;
                  const isEmpty = v === "" || v === null || v === undefined;
                  if (isEmpty) {
                    return allow_empty_option ? new Set([""]) : new Set();
                  }
                  if (!primary_key) {
                    return new Set([String(v)]);
                  }
                  const primaryValue = String(watchedPrimaryValue);
                  const selectedSection = sections.find(
                    (section) => section.key === primaryValue,
                  );
                  if (!selectedSection) {
                    console.error(
                      `No section found for field "${name}" with key "${primaryValue}"`,
                    );
                    return new Set();
                  }
                  const selectedItem = selectedSection.items.find(
                    (item) => item.value === String(v),
                  );
                  if (!selectedItem) {
                    console.error(
                      `No item found for field "${name}" with value "${String(v)}" in section "${primaryValue}"`,
                    );
                    return new Set();
                  }
                  return new Set([
                    buildSectionItemKey(
                      selectedSection.value,
                      selectedItem.label,
                    ),
                  ]);
                })()}
                onChange={(selected) => {
                  const selectedValue = selected.target.value;

                  if (selectedValue === "" || selectedValue === "null") {
                    field.onChange(null);
                    return;
                  }
                  if (!primary_key) {
                    field.onChange(selectedValue);
                    return;
                  }

                  const sectionByItemLabel = new Map<
                    string,
                    (typeof sections)[number]
                  >();

                  sections.forEach((section) => {
                    section.items.forEach((item) => {
                      const key = buildSectionItemKey(
                        section.value,
                        item.label,
                      );
                      // There are duplicate labels among sections. For instance
                      // "structured product" belongs both to "Equity" and "Fixed income".
                      if (sectionByItemLabel.has(key)) {
                        console.error(
                          `Duplicate section mapping for item label: ${item.label}`,
                          {
                            existingSection: sectionByItemLabel.get(item.label),
                            nextSection: section,
                            item,
                          },
                        );
                      }
                      sectionByItemLabel.set(key, section);
                    });
                  });

                  const foundSection = sectionByItemLabel.get(selectedValue);

                  if (foundSection) {
                    setValue(primary_key, foundSection.key);
                    let assetClass2 = stripSectionNameFromKey(selectedValue);
                    let it: SectionItem | undefined = foundSection.items.find(
                      (it) => it.label === assetClass2,
                    );
                    if (it) {
                      field.onChange(it.value);
                    } else {
                      console.error(
                        `Impossible to find item with label ${assetClass2} in ${foundSection.items}`,
                      );
                    }
                    return;
                  }

                  console.error(`Impossible to find key: ${selectedValue}`);
                }}
              >
                <>
                  {allow_empty_option && sections.length > 0 && (
                    <SelectSection key="__empty__">
                      <SelectItem key="" className="h-8" textValue="">
                        {" "}
                      </SelectItem>
                    </SelectSection>
                  )}
                  {sections.map((section) => (
                    <SelectSection
                      key={section.value}
                      title={section.value}
                    >
                      {section.items.map((item) => (
                        <SelectItem
                          key={
                            primary_key
                              ? buildSectionItemKey(section.value, item.label)
                              : item.label
                          }
                          textValue={t(item.label, {
                            defaultValue: primary_key
                              ? buildSectionItemKey(section.value, item.label)
                              : item.label,
                          })}
                        >
                          {t(item.label, {
                            defaultValue: item.label,
                          })}
                        </SelectItem>
                      ))}
                    </SelectSection>
                  ))}
                </>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};

export { SelectFieldWithSection };
